import { useEffect, useState } from 'react'

interface UseMediaDevicesResponse {
  mediaDevices: {
    audioInputs: MediaDeviceInfo[]
    audioOutputs: MediaDeviceInfo[]
  }
  requeryDevices: () => void
}
const useMediaDevices = (): UseMediaDevicesResponse => {
  const [audioInputs, setAudioInputs] = useState<MediaDeviceInfo[]>([])
  const [audioOutputs, setAudioOutputs] = useState<MediaDeviceInfo[]>([])

  const setupDevices = (
    mediaDevices: MediaDeviceInfo[],
    deviceKind: MediaDeviceKind,
    setDevices: (value: React.SetStateAction<MediaDeviceInfo[]>) => void,
  ) => {
    const availableDevices = mediaDevices.filter(
      (device) => device.kind === deviceKind,
    )
    if (availableDevices.length === 0) {
      setDevices([])
      return
    }
    const defaultDevice = availableDevices[0]
    const cleanedDevices = availableDevices.filter(
      (device) => device.groupId !== defaultDevice.groupId,
    )
    setDevices([defaultDevice, ...cleanedDevices])
  }
  const configureAudioInputs = (mediaDevices: MediaDeviceInfo[]) => {
    return setupDevices(mediaDevices, 'audioinput', setAudioInputs)
  }
  const configureAudioOutputs = (mediaDevices: MediaDeviceInfo[]) => {
    return setupDevices(mediaDevices, 'audiooutput', setAudioOutputs)
  }
  const configureAllDevices = (mediaDevices: MediaDeviceInfo[]) => {
    if (mediaDevices.length === 0) return
    configureAudioInputs(mediaDevices)
    configureAudioOutputs(mediaDevices)
  }

  useEffect(() => {
    let mounted = true

    const onChange = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices()
      if (mounted) {
        configureAllDevices(devices)
      }
    }

    navigator.mediaDevices.addEventListener('devicechange', onChange)
    onChange()

    return () => {
      mounted = false
      navigator.mediaDevices.removeEventListener('devicechange', onChange)
    }
  }, [])

  const requeryDevices = async () => {
    const devices = await navigator.mediaDevices.enumerateDevices()
    configureAllDevices(devices)
  }

  return {
    mediaDevices: {
      audioOutputs: audioOutputs,
      audioInputs: audioInputs,
    },
    requeryDevices,
  }
}

export default useMediaDevices
